import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import QRCode from 'qrcode.react'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import type { UI2QrCodeDisplayProps } from 'shared-libs/components/ui2/fields/ui2-qr-code-display/ui2-qr-code-display-props'
import type { IUI2WebFieldBaseExtras } from '../field-ui/i-ui2-web-field-base-extras'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'

import './_ui2-qr-code-display.scss'

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_SIZE = 200
const LIGHTBOX_VW = 70

// ─── Props ────────────────────────────────────────────────────────────────────

export interface IUI2QrCodeDisplayProps extends UI2QrCodeDisplayProps, IUI2WebFieldBaseExtras {}

const DEFAULT_EMPTY_TEXT = 'QR code not available'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveQrValue(props: IUI2QrCodeDisplayProps): string | undefined {
  const { formula, frames, qrValue, value } = props
  if (formula && frames) {
    const evaluated = evaluateExpressionWithScopes(frames as any, formula, CustomFormulas)
    if (evaluated != null && evaluated !== '') return String(evaluated)
  }
  if (qrValue != null && qrValue !== '') return String(qrValue)
  return value != null && value !== '' ? String(value) : undefined
}

function resolveCodeText(props: IUI2QrCodeDisplayProps): string | undefined {
  const { codeDisplayFormula, frames } = props
  if (!codeDisplayFormula || !frames) return undefined
  const evaluated = evaluateExpressionWithScopes(frames as any, codeDisplayFormula, CustomFormulas)
  return evaluated != null && evaluated !== '' ? String(evaluated) : undefined
}

// ─── Component ────────────────────────────────────────────────────────────────

export const UI2QrCodeDisplay: React.FC<IUI2QrCodeDisplayProps> = (props) => {
  const { size = DEFAULT_SIZE } = props

  const qrValue = resolveQrValue(props)
  const codeText = resolveCodeText(props)

  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setIsLightboxOpen(false)
  }, [])

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isLightboxOpen, handleKeyDown])

  const renderBody = useCallback(() => {
    if (!qrValue) return null
    return (
      <div className='ui2-qrCodeDisplay-container'>
        <button
          className='ui2-qrCodeDisplay-tile'
          // Zooming is an interaction like any other — a disabled display
          // field shows its code but doesn't open.
          onClick={props.isDisabled ? undefined : () => setIsLightboxOpen(true)}
          disabled={!!props.isDisabled}
          aria-label='Open QR code'
        >
          <QRCode value={qrValue} size={size} />
          {codeText && (
            <div className='ui2-qrCodeDisplay-chip'>
              <span className='ui2-qrCodeDisplay-chipText'>{codeText}</span>
            </div>
          )}
          {!props.isDisabled && (
            <span className='ui2-qrCodeDisplay-zoomBadge'>
              <Icon icon={IconNames.ZOOM_IN} iconSize={12} />
            </span>
          )}
        </button>
      </div>
    )
  }, [qrValue, codeText, size, props.isDisabled])

  const lightboxSize = Math.round(
    (Math.min(window.innerWidth, window.innerHeight) * LIGHTBOX_VW) / 100,
  )

  const lightbox =
    isLightboxOpen && qrValue
      ? ReactDOM.createPortal(
          <div className='ui2-qrCodeDisplay-lightbox' role='dialog' aria-modal>
            <div className='ui2-qrCodeDisplay-lightbox-backdrop' onClick={() => setIsLightboxOpen(false)} />
            <div className='ui2-qrCodeDisplay-lightbox-content'>
              <div className='ui2-qrCodeDisplay-lightbox-qrTile'>
                <QRCode value={qrValue} size={lightboxSize} />
              </div>
              {codeText && (
                <div className='ui2-qrCodeDisplay-chip'>
                  <span className='ui2-qrCodeDisplay-chipText'>{codeText}</span>
                </div>
              )}
            </div>
            <button
              className='ui2-qrCodeDisplay-lightbox-close'
              onClick={() => setIsLightboxOpen(false)}
              aria-label='Close'
            >
              <Icon icon={IconNames.CROSS} />
            </button>
          </div>,
          document.body,
        )
      : null

  return (
    <UI2FieldShell
      {...props}
      isReadOnly
      isValueEmpty={() => !qrValue}
      emptyText={props.emptyText ?? DEFAULT_EMPTY_TEXT}
      className='ui2-qr-code-display'
      renderReadOnly={(_value, { editNode }) => {
        const body = renderBody()
        if (!body) return null
        return (
          <>
            <div className='ui2-fieldShell-readOnly-content'>{body}</div>
            {editNode}
            {lightbox}
          </>
        )
      }}
    />
  )
}
